import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Form } from 'formik';
import { ArrowRight, Eye, EyeOff } from 'lucide-react';
import { LogoLoader } from '@gokrew/ui';
import { FormField, FormikWithSchema } from '@gokrew/ui/form';
import { PasswordValidationIndicator } from '@gokrew/ui/components/PasswordValidationIndicator';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import { resetPassword } from '@/api/auth';
import type { ResetPasswordForm, resetPasswordResponse } from '@/types/auth';
import { resetPasswordSchema } from '@/validation/authSchema';
import { Button } from '@/components/ui/CustomButton';
import { AuthV2Layout } from '../components/AuthV2Layout';
import layoutStyles from '../components/AuthV2Layout.module.css';
import styles from './ResetPasswordPage.module.css';

interface ResetPasswordFormValues extends ResetPasswordForm {
  confirmPassword: string;
}

export function ResetPasswordV2Page() {
  const navigate = useNavigate();
  const { t } = useTranslation(TranslationNS.Auth);
  const [params] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const token = params.get('token');

  const initialValues: ResetPasswordFormValues = {
    newPassword: '',
    token: token ?? '',
    confirmPassword: '',
  };

  const mutation = useMutation<
    resetPasswordResponse,
    AxiosError<{ message: string }>,
    ResetPasswordForm
  >({
    mutationFn: ({ token, newPassword }) => resetPassword(token!, newPassword),
    onSuccess: () => navigate('/login', { replace: true }),
    onError: (error) => {
      setServerError(
        error.response?.data?.message ??
          t('signup_v2.reset_password_screen.server_error')
      );
    },
  });

  return (
    <AuthV2Layout>
      <div className={layoutStyles.formInner}>
        <div className={layoutStyles.formCard}>
          <h2 className={styles.heading}>
            {t('signup_v2.reset_password_screen.heading')}
          </h2>
          <p className={styles.subtext}>
            {t('signup_v2.reset_password_screen.subtext')}
          </p>

          <FormikWithSchema
            initialValues={initialValues}
            validationSchema={resetPasswordSchema(t)}
            onSubmit={(values) => {
              if (!token) {
                setServerError(t('invalid_token'));
                navigate('/login');
                return;
              }
              setServerError(null);
              mutation.mutate({ token, newPassword: values.newPassword });
            }}
          >
            {({ values }) => (
              <Form className={styles.form}>
                <FormField
                  name="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  label={t('signup_v2.reset_password_screen.new_password_label')}
                  placeholder="••••••••"
                  IconSuffix={showPassword ? Eye : EyeOff}
                  onIconClick={() => setShowPassword((s) => !s)}
                />

                <FormField
                  name="confirmPassword"
                  type={showConfirm ? 'text' : 'password'}
                  label={t('signup_v2.reset_password_screen.confirm_password_label')}
                  placeholder="••••••••"
                  IconSuffix={showConfirm ? Eye : EyeOff}
                  onIconClick={() => setShowConfirm((s) => !s)}
                />

                <PasswordValidationIndicator
                  password={values.newPassword}
                  confirmPassword={values.confirmPassword}
                  showMatchValidation={true}
                />

                {serverError && (
                  <p className={styles.serverError}>{serverError}</p>
                )}

                <Button
                  type="submit"
                  className={styles.submitBtn}
                  IconSuffix={mutation.isPending ? undefined : ArrowRight}
                  disabled={mutation.isPending}
                >
                  {mutation.isPending ? (
                    <LogoLoader size="sm" />
                  ) : (
                    t('signup_v2.reset_password_screen.submit')
                  )}
                </Button>
              </Form>
            )}
          </FormikWithSchema>

          <p className={styles.backRow}>
            <Link to="/login" className={styles.backLink}>
              {t('signup_v2.reset_password_screen.back_link')}
            </Link>
          </p>
        </div>
      </div>
    </AuthV2Layout>
  );
}
